// Space — this device's membership in "my space": device keys, secrets, journal, notes, sync.
// One instance per page (the app keeps it across screens so sync runs while you browse).

import { IdbStore, type Store } from './store';
import { Journal } from './journal';
import { createDevice, newSpace, type LocalDevice, type SpaceSecrets } from './keys';
import { revokeAndRekey } from './rekey';
import { SpaceSync } from './sync';
import { Notes } from '../notes/notes';

/** A conversation announced in my space (docs/phase-3-messages.md). */
export type ConvRecord = {
  conv: string;
  secrets: SpaceSecrets;
  ts: number;
  kind?: 'direct' | 'group';
  /** The contact, for a direct conversation. */
  peer?: { identity: string; name: string };
  /** The group name at invitation time (then group.meta inside the group). */
  name?: string;
  /** I left this group: hidden, kept in sync only until my departure is processed. */
  left?: boolean;
  /** My copy is gone (departure processed): never reopen it. */
  closed?: boolean;
};

/**
 * Events: 'revoked' {device} (a device of mine was removed from here), 'conv' {rec, isNew}
 * (a conversation was announced, or re-announced with new key epochs).
 *
 * Conversation announcements are recorded here, from construction on: on a freshly linked device
 * sync starts at once, and they must not be missed before the conversations manager is ready.
 */
export class Space extends EventTarget {
  readonly journal: Journal;
  readonly notes: Notes;
  readonly convs = new Map<string, ConvRecord>();
  /** My display name for contacts (last writer wins; ts 0 = never chosen, device name used). */
  profile: { name: string; ts: number } = { name: '', ts: 0 };
  /** My private nicknames for contacts, by identity key. */
  readonly aliases = new Map<string, { name: string | null; ts: number }>();
  sync: SpaceSync | null = null;
  #convTasks: Promise<void> = Promise.resolve();

  private constructor(
    readonly store: Store,
    readonly device: LocalDevice,
    secrets: SpaceSecrets,
  ) {
    super();
    this.journal = new Journal(store, device, secrets);
    this.notes = new Notes(store, this.journal);
    this.journal.addEventListener('envelope', (e) => {
      const { payload } = (e as CustomEvent).detail;
      if (payload.type === 'profile.set' && payload.ts > this.profile.ts && typeof payload.name === 'string') {
        this.profile = { name: payload.name.slice(0, 60), ts: payload.ts };
        void this.store.setKv('profile', this.profile);
        this.dispatchEvent(new Event('profile'));
      }
      if (payload.type === 'contact.alias' && payload.ts > (this.aliases.get(payload.person)?.ts ?? 0)) {
        this.aliases.set(payload.person, { name: payload.name ? String(payload.name).slice(0, 60) : null, ts: payload.ts });
        void this.store.setKv('aliases', [...this.aliases]);
        this.dispatchEvent(new Event('profile'));
      }
      if (payload.type === 'conv.leave') {
        this.#convTasks = this.#convTasks.then(() => this.#leftConv(payload.conv)).catch(console.error);
        return;
      }
      if (payload.type !== 'conv.add') return;
      const { conv, secrets, peer, kind, name, ts } = payload;
      this.#convTasks = this.#convTasks.then(() => this.#recordConv({ conv, secrets, peer, kind: kind ?? 'direct', name, ts })).catch(console.error);
    });
  }

  /** The name my contacts see: chosen, or this device's name until one is chosen. */
  get displayName(): string {
    return this.profile.name || this.device.name;
  }

  /** Resolves once conversation announcements received so far are recorded. */
  convsSettled(): Promise<void> {
    return this.#convTasks;
  }

  /** The departure was processed (my devices were removed from the group): forget it here. */
  async markClosed(conv: string): Promise<void> {
    const rec = this.convs.get(conv);
    if (!rec || rec.closed) return;
    rec.closed = true;
    await this.store.setKv('convs', [...this.convs.values()]);
  }

  async #leftConv(conv: string): Promise<void> {
    const rec = this.convs.get(conv);
    if (!rec || rec.left) return;
    rec.left = true;
    await this.store.setKv('convs', [...this.convs.values()]);
    this.dispatchEvent(new CustomEvent('conv-left', { detail: { conv } }));
  }

  async #recordConv(rec: ConvRecord): Promise<void> {
    const known = this.convs.get(rec.conv);
    if (known?.left && !known.closed) return; // leaving: wait until it is processed
    if (known?.closed) {
      // Invited back after leaving: start over with the new secrets.
      this.convs.delete(rec.conv);
      return this.#recordConv(rec);
    }
    if (!known) {
      this.convs.set(rec.conv, structuredClone(rec));
    } else {
      let grew = false;
      for (const [epoch, secret] of Object.entries(rec.secrets.secrets)) {
        if (!(Number(epoch) in known.secrets.secrets)) {
          known.secrets.secrets[Number(epoch)] = secret;
          grew = true;
        }
      }
      if (rec.secrets.epoch > known.secrets.epoch) {
        known.secrets.epoch = rec.secrets.epoch;
        grew = true;
      }
      if (!grew) return;
    }
    await this.store.setKv('convs', [...this.convs.values()]);
    this.dispatchEvent(new CustomEvent('conv', { detail: { rec: this.convs.get(rec.conv), isNew: !known } }));
  }

  /** The space stored on this device, or null if it has none yet. */
  static async open(store: Store = new IdbStore()): Promise<Space | null> {
    const device = await store.getKv<LocalDevice>('device');
    const secrets = await store.getKv<SpaceSecrets>('space');
    if (!device || !secrets) return null;
    const space = new Space(store, device, secrets);
    await space.journal.load();
    await space.notes.load();
    for (const rec of (await store.getKv<ConvRecord[]>('convs')) ?? []) space.convs.set(rec.conv, rec);
    space.profile = (await store.getKv<{ name: string; ts: number }>('profile')) ?? space.profile;
    for (const [p, a] of (await store.getKv<[string, { name: string | null; ts: number }][]>('aliases')) ?? []) space.aliases.set(p, a);
    return space;
  }

  /**
   * Notes and journal data left on this device by a previous space (its keys gone). Creating or
   * joining a space must never silently write over them.
   */
  static async leftovers(store: Store = new IdbStore()): Promise<number> {
    const notes = (await store.allIndex()).length;
    const heads = (await store.getKv<unknown[]>('heads'))?.length ?? 0;
    return notes || heads ? Math.max(notes, 1) : 0;
  }

  static async create(name: string, store: Store = new IdbStore()): Promise<Space> {
    if (await Space.leftovers(store)) throw new Error('leftover data from a previous space');
    const device = await createDevice(name);
    const secrets = await newSpace();
    await store.setKv('device', device);
    await store.setKv('space', secrets);
    const space = new Space(store, device, secrets);
    await space.journal.genesis();
    return space;
  }

  /** After linking: the device keys were made for the link, the secrets came from the host. */
  static async join(device: LocalDevice, secrets: SpaceSecrets, store: Store = new IdbStore()): Promise<Space> {
    if (await Space.leftovers(store)) throw new Error('leftover data from a previous space');
    await store.setKv('device', device);
    await store.setKv('space', secrets);
    return new Space(store, device, secrets);
  }

  startSync(wsUrl: (path: string) => string, iceServers: () => Promise<RTCIceServer[]>): SpaceSync {
    this.sync ??= new SpaceSync(this.journal, wsUrl, iceServers);
    void this.sync.start();
    return this.sync;
  }

  /** Waits until this device's admission (device.add) has arrived through sync. */
  whenMember(): Promise<void> {
    if (this.journal.isMember) return Promise.resolve();
    return new Promise((resolve) => {
      const check = () => {
        if (!this.journal.isMember) return;
        this.journal.removeEventListener('envelope', check);
        resolve();
      };
      this.journal.addEventListener('envelope', check);
    });
  }

  /**
   * Removes a device: its envelopes after the last one we know are refused, then a new space
   * secret is sealed for every remaining device (the revoked one cannot read it), so it loses
   * the room and the content key from now on. It keeps whatever it already had.
   */
  async revoke(deviceId: string): Promise<void> {
    await revokeAndRekey(this.journal, deviceId);
    await this.sync?.refreshRooms();
    this.dispatchEvent(new CustomEvent('revoked', { detail: { device: deviceId } }));
  }

  async leave(): Promise<void> {
    this.sync?.stop();
    await this.store.clear();
  }
}
